/**
 * NavigationLink Component
 * ========================
 * Clickable card that navigates to app screens.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plane,
    Calculator,
    Calendar,
    Map,
    Cloud,
    Zap,
    Brain,
    Radio,
    Play,
    LayoutDashboard,
    GitBranch,
    Clock,
    Home,
    Settings,
    Folder,
    Link as LinkIcon,
    ExternalLink
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export interface NavigationLinkData {
    type: string;
    name: string;
    path: string;
    icon: string;
    category?: string;
}

interface NavigationLinkProps {
    link: NavigationLinkData;
    onNavigate?: () => void;
}

const NavigationLink: React.FC<NavigationLinkProps> = ({ link, onNavigate }) => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const getIcon = () => {
        const iconClass = "w-5 h-5";
        switch (link.icon) {
            case 'plane': return <Plane className={iconClass} />;
            case 'calculator': return <Calculator className={iconClass} />;
            case 'calendar': return <Calendar className={iconClass} />;
            case 'map': return <Map className={iconClass} />;
            case 'cloud': return <Cloud className={iconClass} />;
            case 'zap': return <Zap className={iconClass} />;
            case 'brain': return <Brain className={iconClass} />;
            case 'radio': return <Radio className={iconClass} />;
            case 'play': return <Play className={iconClass} />;
            case 'layout-dashboard': return <LayoutDashboard className={iconClass} />;
            case 'git-branch': return <GitBranch className={iconClass} />;
            case 'clock': return <Clock className={iconClass} />;
            case 'home': return <Home className={iconClass} />;
            case 'settings': return <Settings className={iconClass} />;
            case 'folder': return <Folder className={iconClass} />;
            default: return <LinkIcon className={iconClass} />;
        }
    };

    const getCategoryColor = () => {
        switch (link.category) {
            case 'Operations': return 'from-blue-500 to-cyan-500';
            case 'Pre-tactical': return 'from-purple-500 to-pink-500';
            case 'AI Modules': return 'from-emerald-500 to-teal-500';
            case 'Integration': return 'from-orange-500 to-amber-500';
            case 'Simulations': return 'from-rose-500 to-red-500';
            default: return 'from-gray-500 to-slate-500';
        }
    };

    const handleClick = () => {
        navigate(link.path);
        onNavigate?.();
    };

    const cardBg = isDark ? 'bg-[#0a2540]' : 'bg-white';
    const borderColor = isDark ? 'border-[#1e3a5f]' : 'border-[var(--border)]';
    const textPrimary = 'text-[var(--text-primary)]';
    const textSecondary = 'text-[var(--text-secondary)]';

    return (
        <button
            onClick={handleClick}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border ${borderColor} ${cardBg}
                       hover:border-[#F26430] hover:shadow-lg hover:shadow-[#F26430]/10 
                       transition-all duration-200 group text-left`}
        >
            {/* Icon */}
            <div className={`p-2 rounded-lg bg-gradient-to-br ${getCategoryColor()} text-white
                           group-hover:scale-110 transition-transform`}>
                {getIcon()}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <p className={`font-medium truncate ${textPrimary}`}>{link.name}</p>
                {link.category && (
                    <p className={`text-xs ${textSecondary}`}>{link.category}</p>
                )}
            </div>

            {/* Arrow */}
            <ExternalLink className={`w-4 h-4 ${textSecondary} group-hover:text-[#F26430] 
                                     group-hover:translate-x-1 transition-all`} />
        </button>
    );
};

export default NavigationLink;
